import { createRouter, createWebHistory, type LocationQuery, type RouteLocationRaw, type RouteRecordRaw } from 'vue-router';
import { DEFAULT_PLAN_TAB, navigableDestinations, resolvePlanTab } from './destinations';
import { ensureSession, useSessionStore } from '@/stores/session.store';
import { companyEntryDecision } from '@/domain/company-selection';

/**
 * Registro de componente por destino implementado. Cada etapa que entrega uma família adiciona sua
 * entrada aqui — nunca uma rota sem componente real (ver destinations.ts).
 */
const VIEW_BY_DESTINATION: Record<string, () => Promise<{ default: unknown }>> = {
  inicio: () => import('@/views/InicioView.vue'),
  manutencoes: () => import('@/views/ManutencoesView.vue'),
  plano: () => import('@/views/PlanoView.vue'),
  clinica: () => import('@/views/ClinicaView.vue'),
  perfil: () => import('@/views/PerfilView.vue'),
};

/** Compatibilidade das rotas do protótipo, mantendo somente a query recebida pelo deep link. */
export function entryLoginRedirect(query: LocationQuery): RouteLocationRaw {
  return { path: '/login', query };
}

/** Compatibilidade para a etapa de primeira senha do protótipo. */
export function setupPasswordRedirect(query: LocationQuery): RouteLocationRaw {
  return { path: '/senha', query: { ...query, mode: 'setup' } };
}

function buildRoutes(): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = [];
  for (const destination of navigableDestinations) {
    const loadView = VIEW_BY_DESTINATION[destination.name];
    if (!loadView) {
      // Falha cedo e alto: melhor derrubar o boot do que renderizar um item de menu que leva a nada.
      throw new Error(`Destino "${destination.name}" está marcado implemented:true mas não tem view registrada em router/index.ts.`);
    }
    routes.push({ path: destination.path, name: destination.name, component: loadView });
  }
  return routes;
}

export const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: () => ({ top: 0 }),
  routes: [
    { path: '/', redirect: '/inicio' },
    // Públicas (sem sessão): login, callback do PKCE e as rotas de entrada/identidade do
    // corte 1 — todas standalone (sem shell).
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { public: true, standalone: true } },
    // Endereço do protótipo; mantém query de deep links como `redirect` na rota canônica.
    { path: '/entrada', redirect: (to) => entryLoginRedirect(to.query) },
    { path: '/auth/callback', name: 'auth-callback', component: () => import('@/views/AuthCallbackView.vue'), meta: { public: true, standalone: true } },
    // Indisponibilidade do BFF em reload frio (guard `unreachable`). `public` para não recursar
    // no próprio guard; `standalone` mantém a tela fora do shell, como login e callback.
    { path: '/indisponivel', name: 'indisponivel', component: () => import('@/views/IndisponivelView.vue'), meta: { public: true, standalone: true } },
    { path: '/cadastro', name: 'cadastro', component: () => import('@/views/CadastroView.vue'), meta: { public: true, standalone: true } },
    { path: '/senha', name: 'senha', component: () => import('@/views/SenhaView.vue'), meta: { public: true, standalone: true } },
    // Compatibilidade com a referência `#/senha/setup`, preservando e-mail e redirect na URL.
    { path: '/senha/setup', redirect: (to) => setupPasswordRedirect(to.query) },
    { path: '/privacidade', name: 'privacidade', component: () => import('@/views/PrivacidadeView.vue'), meta: { public: true, standalone: true } },
    // Pós-login, antes do shell (multi-unidade): autenticadas, standalone.
    { path: '/clinicas', name: 'clinicas', component: () => import('@/views/ClinicasView.vue'), meta: { standalone: true } },
    { path: '/clinicas/nova', name: 'clinicas-nova', component: () => import('@/views/ClinicaNovaView.vue'), meta: { standalone: true } },
    ...buildRoutes(),
    // Detalhe do chamado — sub-rota da aba Manutenções (a aba continua acesa).
    {
      path: '/manutencoes/:id',
      name: 'manutencoes-detalhe',
      component: () => import('@/views/ManutencoesDetalheView.vue'),
      meta: { activeDestination: 'manutencoes' },
    },
    // Ciclo do chamado: abertura, busca e confirmação (CLI-TEL-004/005/006). `/confirmado` é
    // rota própria porque o deep link do push precisa de endereço (?requestId=).
    {
      path: '/abrir',
      name: 'abrir',
      component: () => import('@/views/AbrirView.vue'),
      meta: { activeDestination: 'manutencoes' },
    },
    {
      path: '/buscando',
      name: 'buscando',
      component: () => import('@/views/BuscandoView.vue'),
      meta: { activeDestination: 'manutencoes' },
    },
    {
      path: '/confirmado',
      name: 'confirmado',
      component: () => import('@/views/ConfirmadoView.vue'),
      meta: { activeDestination: 'manutencoes' },
    },
    // Proposta é filha de "Meu plano" no protótipo (`/plano/proposta`, com voltar para a aba
    // Contrato) e mantém a aba acesa na navegação.
    {
      path: '/plano/proposta',
      name: 'plano-proposta',
      component: () => import('@/views/PropostaView.vue'),
      meta: { activeDestination: 'plano' },
    },
    // Aba do "Meu plano" refletida na URL. A forma CANÔNICA é a capitalizada, porque é o dialeto
    // que o servidor já emite no campo `Route` do portal-summary; a minúscula é aceita e
    // redirecionada, para deep link antigo continuar funcionando.
    {
      path: '/plano/:tab',
      name: 'plano-tab',
      component: () => import('@/views/PlanoView.vue'),
      meta: { activeDestination: 'plano' },
      beforeEnter: (to): RouteLocationRaw | boolean => {
        const raw = typeof to.params['tab'] === 'string' ? to.params['tab'] : '';
        const tab = resolvePlanTab(raw);
        if (tab === null) return { path: `/plano/${DEFAULT_PLAN_TAB}`, replace: true };
        if (tab !== raw) return { path: `/plano/${tab}`, query: to.query, replace: true };
        return true;
      },
    },
    // Sub-rota do Perfil: meus dados e alteração de senha.
    {
      path: '/perfil/conta',
      name: 'perfil-conta',
      component: () => import('@/views/ContaEditView.vue'),
      meta: { activeDestination: 'perfil' },
    },
    {
      path: '/perfil/senha',
      name: 'perfil-senha',
      component: () => import('@/views/PerfilSenhaView.vue'),
      meta: { activeDestination: 'perfil' },
    },
    // Ajuda da clínica: lista, abertura e conversa pertencem ao Perfil, sem criar item no shell.
    {
      path: '/suporte',
      name: 'suporte',
      component: () => import('@/views/SuporteView.vue'),
      meta: { activeDestination: 'perfil' },
    },
    {
      path: '/suporte/novo',
      name: 'suporte-novo',
      component: () => import('@/views/SuporteNovoView.vue'),
      meta: { activeDestination: 'perfil' },
    },
    {
      path: '/suporte/:id',
      name: 'suporte-detalhe',
      component: () => import('@/views/SuporteDetalheView.vue'),
      meta: { activeDestination: 'perfil' },
    },
    // Auditoria inicial (F22) — fecha um dos escapes do go_router do Flutter (rota própria).
    {
      path: '/plano/Documentos/auditoria',
      name: 'plano-auditoria',
      component: () => import('@/views/AuditoriaInicialView.vue'),
      meta: { activeDestination: 'plano' },
    },
    // Contratação (F87/F88) e renovação (F74) — rotas próprias, fora das abas.
    {
      path: '/plano/catalogo',
      name: 'plano-catalogo',
      component: () => import('@/views/CatalogoView.vue'),
      meta: { activeDestination: 'plano' },
    },
    {
      path: '/plano/catalogo/:planId',
      name: 'plano-pedido',
      component: () => import('@/views/PedidoView.vue'),
      meta: { activeDestination: 'plano' },
    },
    {
      path: '/plano/renovacao',
      name: 'plano-renovacao',
      component: () => import('@/views/RenovacaoView.vue'),
      meta: { activeDestination: 'plano' },
    },
    // Checkout hospedado do chamado (F86).
    {
      path: '/manutencoes/:id/pagar',
      name: 'manutencoes-pagar',
      component: () => import('@/views/CheckoutView.vue'),
      meta: { activeDestination: 'manutencoes' },
    },
    // Minha clínica — seções com rota própria (fecham os escapes do go_router do Flutter).
    {
      path: '/clinica/Equipamentos',
      name: 'clinica-equipamentos',
      component: () => import('@/views/EquipamentosView.vue'),
      meta: { activeDestination: 'clinica' },
    },
    {
      path: '/clinica/Equipamentos/novo',
      name: 'clinica-equipamento-novo',
      component: () => import('@/views/EquipamentoFormView.vue'),
      meta: { activeDestination: 'clinica' },
    },
    {
      path: '/clinica/Reserva',
      name: 'clinica-reserva',
      component: () => import('@/views/ReservaView.vue'),
      meta: { activeDestination: 'clinica' },
    },
    {
      path: '/clinica/PMOC',
      name: 'clinica-pmoc',
      component: () => import('@/views/PmocView.vue'),
      meta: { activeDestination: 'clinica' },
    },
    {
      path: '/clinica/Equipe',
      name: 'clinica-equipe',
      component: () => import('@/views/EquipeView.vue'),
      meta: { activeDestination: 'clinica' },
    },
    {
      path: '/clinica/Equipe/novo',
      name: 'clinica-equipe-novo',
      component: () => import('@/views/EquipeConviteView.vue'),
      meta: { activeDestination: 'clinica' },
    },
    {
      path: '/clinica/Equipe/favorito',
      name: 'clinica-equipe-favorito',
      component: () => import('@/views/FavoritoView.vue'),
      meta: { activeDestination: 'clinica' },
    },
    // Conveniências: relatórios com export (capacidade que só o Flutter conhecia).
    {
      path: '/relatorios',
      name: 'relatorios',
      component: () => import('@/views/RelatoriosView.vue'),
      meta: { activeDestination: 'inicio' },
    },
    { path: '/:pathMatch(.*)*', redirect: '/inicio' },
  ],
});

router.beforeEach(async (to) => {
  if (to.meta.public === true) return true;
  const result = await ensureSession();
  // `unavailable` = blip do BFF COM sessão em memória: as views já têm dados, a navegação segue
  // e o erro aparece no estado da própria view (AsyncState), sem deslogar ninguém.
  if (result.status === 'unauthenticated') return { name: 'login', query: { redirect: to.fullPath } };
  // Reload frio com o BFF fora do ar: nada em memória para montar o shell. Tela dedicada de
  // indisponibilidade, com retry que devolve o destino pretendido — nunca login (a sessão pode
  // estar válida) nem shell vazio.
  if (result.status === 'unreachable') return { name: 'indisponivel', query: { redirect: to.fullPath } };
  if (result.status === 'unavailable') return true;
  // Multi-clínica (C3): sem unidade resolvível, qualquer rota dentro do app vai para a seleção
  // (ou criação) de clínica — o usuário com 0 ou 2+ unidades não entra no shell por engano.
  const { user } = useSessionStore();
  const decision = companyEntryDecision(
    (user.value?.companies ?? []).map((company) => company.id),
    user.value?.activeCompanyId,
  );
  if (decision.kind !== 'ready') {
    if (to.name === 'clinicas' || to.name === 'clinicas-nova') return true;
    return { name: 'clinicas' };
  }
  return true;
});
